/* The DreamCloud module: problems, numbers and fixes for the salon's
 * DreamCloud worker, read through the `dreamcloud` edge function.
 *
 * Layout A from the design ("problems first"): what needs attention, then the
 * numbers with their chart, then the scheduled jobs beside the actions. The
 * data is js/dreamcloud.js (the store, polled while this page is on screen);
 * the arithmetic and wording are js/rules/dreamcloud.js.
 *
 * Offered only to salons whose licence includes `dreamcloud`, and only owners
 * and platform admins get past the edge function; see apps.js for the first
 * and server/dreamcloud/README.md for the second. A 403 or a missing connection
 * replaces the page with one card saying which.
 */
import * as Vue from 'vue';
import { apps } from '../apps.js';
import { dreamcloud } from '../dreamcloud.js';
import { formatWhen } from '../rules/dreamcloud.js';
import { DcAttention } from './dc-attention.js';
import { DcNumbers } from './dc-numbers.js';
import { DcJobs } from './dc-jobs.js';
import { DcActions } from './dc-actions.js';

var DreamCloudPage = {
  name: 'DreamCloudPage',
  components: { DcAttention: DcAttention, DcNumbers: DcNumbers, DcJobs: DcJobs, DcActions: DcActions },
  setup: function () {
    var state = dreamcloud.state;
    var replayRequest = Vue.ref(null);

    Vue.onMounted(function () {
      dreamcloud.load();
      dreamcloud.startPolling();
    });
    Vue.onUnmounted(dreamcloud.stopPolling);

    function onReplay(r) {
      replayRequest.value = Object.assign({ at: Date.now() }, r);
      var el = document.getElementById('dc-actions');
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return {
      state: state,
      replayRequest: replayRequest,
      onReplay: onReplay,
      updated: Vue.computed(function () { return state.lastOkAt ? formatWhen(state.lastOkAt) : null; })
    };
  },
  template: [
    '<div class="dc">',
    '  <header class="dc-head">',
    '    <h2 class="dc-title">DreamCloud</h2>',
    '    <span v-if="updated" class="dc-muted">Updated {{ updated }}</span>',
    '  </header>',
    '',
    '  <div v-if="state.notConnected" class="dc-card dc-notice" role="status">',
    '    <p class="dc-notice__lead">DreamCloud isn&rsquo;t connected for this salon.</p>',
    '    <p class="dc-muted">A platform admin connects it in the SQL editor of the TurnWizard',
    '      Supabase project, as the service role, with',
    '      <code>dreamcloud_set_connection(tenant, worker address, dashboard key)</code>.',
    '      See <code>server/dreamcloud/README.md</code> &rarr; Connecting a salon.</p>',
    '  </div>',
    '',
    '  <div v-else-if="state.forbidden" class="dc-card dc-notice" role="status">',
    '    <p class="dc-notice__lead">{{ state.forbidden }}</p>',
    '  </div>',
    '',
    /* A write still on its way keeps the page mounted under the notice, so a
       403 or 409 from a refresh mid-send does not unmount the dialog and lose
       what the send did. */
    '  <template v-if="(!state.notConnected && !state.forbidden) || state.writing">',
    '    <dc-attention @replay="onReplay" />',
    '    <dc-numbers />',
    '    <div class="dc-lower">',
    '      <dc-jobs />',
    '      <dc-actions :replay-request="replayRequest" />',
    '    </div>',
    '  </template>',
    '</div>'
  ].join('\n')
};

apps.register({
  id: 'dreamcloud',
  name: 'DreamCloud',
  icon: '☁',
  blurb: 'Problems, numbers and fixes for the DreamCloud worker.',
  page: DreamCloudPage
});

export { DreamCloudPage };
